import { __ } from '@wordpress/i18n';
import {
  useBlockProps,
  useInnerBlocksProps,
  store as blockEditorStore,
} from '@wordpress/block-editor';
import { Button, Notice } from '@wordpress/components';
import { useSelect, useDispatch } from '@wordpress/data';
import { createBlock } from '@wordpress/blocks';
import Inspector from './InspectorControls';
import { getWrapperClasses, getWrapperStyles } from './styles';
import './style.scss';

const ALLOWED_BLOCKS = ['brandy/icon-list-item'];

/**
 * Seeded lines, each with real text.
 *
 * An empty Icon List is three invisible rows: the icons are there but the
 * words are not, so neither the gap controls nor the layout switch have
 * anything to act on. Mirrors block.json's `example`.
 */
const DEFAULT_ITEMS = [
  { text: __('Free delivery over $100', 'brandy-blocks') },
  { text: __('Thirty-day returns, no questions', 'brandy-blocks') },
  { text: __('Two-year warranty on every part', 'brandy-blocks') },
];

const TEMPLATE = DEFAULT_ITEMS.map((attributes) => [
  'brandy/icon-list-item',
  attributes,
]);

export default function Edit({ attributes, setAttributes, clientId }) {
  const blockProps = useBlockProps({
    className: getWrapperClasses(attributes),
    style: getWrapperStyles(attributes),
  });

  // A list with no children renders an empty <ul> — valid markup, invisible
  // page, and indistinguishable from a broken block. Surface it with a
  // one-click way back: the inner-block template only runs on first insert, so
  // a list whose items were all deleted otherwise has no recovery path.
  // Same posture as Social Icons.
  const hasItems = useSelect(
    (select) => select(blockEditorStore).getBlocks(clientId).length > 0,
    [clientId]
  );
  const { replaceInnerBlocks } = useDispatch(blockEditorStore);

  const addDefaultItems = () =>
    replaceInnerBlocks(
      clientId,
      DEFAULT_ITEMS.map((item) => createBlock('brandy/icon-list-item', item)),
      false
    );

  const innerBlocksProps = useInnerBlocksProps(blockProps, {
    allowedBlocks: ALLOWED_BLOCKS,
    template: TEMPLATE,
    orientation: attributes.layout === 'horizontal' ? 'horizontal' : 'vertical',
  });

  return (
    <>
      <Inspector attributes={attributes} setAttributes={setAttributes} />

      {!hasItems && (
        <Notice status="warning" isDismissible={false}>
          {__('This list is empty and will not appear on the page.', 'brandy-blocks')}{' '}
          <Button variant="link" onClick={addDefaultItems}>
            {__('Add lines', 'brandy-blocks')}
          </Button>
        </Notice>
      )}

      {/* The list element IS the wrapper, matching save.jsx, so gap and
          alignment land on the same node in both runtimes. */}
      <ul {...innerBlocksProps} />
    </>
  );
}
